import PropTypes from 'prop-types';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Link from '@zenbusiness/zui/Link';
import SkeletonGrid from '@zenbusiness/zui/SkeletonGrid';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { filterProductCardsByFeatureFlags, useProductCardFeatureFlags } from '#client/utils/productCardFlags';

import * as Styled from './ProductCards.style';

// Gusto product card destination override — points the card at the new internal
// Gusto landing page (src/pages/ProductLanding/constants/gusto.js) instead of
// whatever primaryPath/primaryExternal the backend currently returns for it.
const PRODUCT_PRIMARY_PATH_OVERRIDES = {
  'product.name.gusto': '/dashboard/{BUUID}/products/gusto',
};

function ProductCards({ items, loading, elementPosition = 'products_page', columns = 3 }) {
  const { formatMessage: fm } = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { businessEntityUuid } = useBusinessEntity();
  const featureFlagValues = useProductCardFeatureFlags();

  const visibleItems = filterProductCardsByFeatureFlags(items, featureFlagValues);

  if (loading) {
    return <SkeletonGrid columns={isDesktop ? 3 : 2} count={isDesktop ? 9 : 6} />;
  }

  if (visibleItems.length === 0) {
    return null;
  }
  return (
    <Styled.ListCard columns={columns}>
      {visibleItems.map(({ titleKey, descriptionKey, imagePath, primaryExternal, primaryPath }) => {
        const title = fm({ id: titleKey });
        const description = fm({ id: descriptionKey });
        const primaryPathOverride = PRODUCT_PRIMARY_PATH_OVERRIDES[titleKey];
        const resolvedPrimaryPath = primaryPathOverride || primaryPath;
        const resolvedPrimaryExternal = primaryPathOverride ? undefined : primaryExternal;

        return (
          <Styled.ProductListItem key={titleKey}>
            <Styled.ProductCard
              aria-label={title}
              clickstreamProps={{
                elementName: 'product_card',
                elementPosition,
                elementValue: title,
                destination: resolvedPrimaryPath?.replace('{BUUID}', businessEntityUuid) || resolvedPrimaryExternal,
              }}
              component={Link}
              data-testid="product-card-list-item"
              title={title}
              size="xl"
              {...(resolvedPrimaryExternal && { target: '_blank', href: resolvedPrimaryExternal })}
              {...(resolvedPrimaryPath && { to: resolvedPrimaryPath.replace('{BUUID}', businessEntityUuid) })}
              variant="elevation"
            >
              <Stack alignItems="left" spacing={2}>
                <Styled.CardImageContainer>
                  <Styled.CardImage alt="" src={imagePath} />
                </Styled.CardImageContainer>
                {resolvedPrimaryExternal && (
                  <Styled.ExternalIconContainer>
                    <OpenInNewIcon aria-hidden="true" />
                  </Styled.ExternalIconContainer>
                )}
                <Stack gap={0.5}>
                  <Text size="sm" color="primary" weight="medium">
                    {title}
                  </Text>
                  <Text size="xs" color="secondary" weight="regular">
                    {description}
                  </Text>
                </Stack>
              </Stack>
            </Styled.ProductCard>
          </Styled.ProductListItem>
        );
      })}
    </Styled.ListCard>
  );
}

ProductCards.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      titleKey: PropTypes.string,
      descriptionKey: PropTypes.string,
      imagePath: PropTypes.string,
      primaryExternal: PropTypes.string,
      primaryPath: PropTypes.string,
    }),
  ).isRequired,
  elementPosition: PropTypes.string,
  columns: PropTypes.number,
};

export default ProductCards;
